import React from "react";
import { Carousel } from "@mantine/carousel";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowRight,
  BadgeCheck,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  FileCheck2,
  HandCoins,
  Quote,
  ShieldCheck,
  Users,
  CheckCircle,
  Wallet,
  Shield,
  Zap,
} from "lucide-react";
import { Link } from "react-router-dom";
import {
  imgAvatar,
  imgDashboard,
  imgFrame234,
  imgLadyJustice,
} from "../../assets";
import Partners from "../../components/Partners";

function Hero() {
  const MotionLink = motion(Link);
  return (
    <motion.section
      className="hero relative overflow-hidden"
      data-node-id="201:1874"
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.35 }}
      variants={{
        hidden: { opacity: 0 },
        show: { opacity: 1, transition: { staggerChildren: 0.12 } },
      }}
    >
      <motion.img
        className="hero__bg"
        src={imgFrame234}
        alt=""
        variants={{ hidden: { opacity: 0 }, show: { opacity: 1 } }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      />
      <div className="container hero__inner">
        <motion.div
          className="hero__content max-w-[720px]"
          data-node-id="201:1873"
          variants={{
            hidden: { opacity: 0, y: 12 },
            show: {
              opacity: 1,
              y: 0,
              transition: { staggerChildren: 0.12, delayChildren: 0.05 },
            },
          }}
        >
          <motion.h1
            className="hero__title tracking-tight"
            data-node-id="198:1715"
            variants={{
              hidden: { opacity: 0, y: 12 },
              show: { opacity: 1, y: 0 },
            }}
            transition={{ duration: 0.55, ease: "easeOut" }}
          >
            Transforming Property Transactions
            <br />
            Through Digital Innovation.
          </motion.h1>
          <motion.div
            variants={{
              hidden: { opacity: 0, y: 12 },
              show: { opacity: 1, y: 0 },
            }}
            transition={{ duration: 0.55, ease: "easeOut" }}
          >
            <MotionLink
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.98 }}
              className="button hero__cta transition-transform duration-200"
              data-node-id="201:1868"
              to="/signup"
            >
              Get Started
            </MotionLink>
          </motion.div>
        </motion.div>

        <motion.img
          className="hero__statue drop-shadow-sm"
          src={imgLadyJustice}
          alt=""
          data-node-id="219:143"
          variants={{
            hidden: { opacity: 0, x: 14 },
            show: { opacity: 1, x: 0 },
          }}
          transition={{ duration: 0.7, ease: "easeOut" }}
        />
      </div>
    </motion.section>
  );
}

function Features() {
  type FeatureTabKey = "documentation" | "fees" | "disbursement";

  const [activeTab, setActiveTab] =
    React.useState<FeatureTabKey>("documentation");

  const tabContent: Record<
    FeatureTabKey,
    { lead: string; headline: string; label: string }
  > = {
    documentation: {
      label: "Digital Documentation",
      lead: "The core elements in this system is to enhance efficiency, transparency, and professionalism across the legal property market",
      headline:
        "Centralised verification and secure record keeping for all property documents",
    },
    fees: {
      label: "Fee Compliance",
      lead: "The core elements in this system is to enhance fee transparency, enforce NBA standards, and reduce disputes across property transactions",
      headline:
        "Automatic application of NBA-approved fees preventing undercutting while allowing premium rates",
    },
    disbursement: {
      label: "Automated Disbursement",
      lead: "The core elements in this system is to enhance secure payments, improve accountability, and streamline allocations across transactions",
      headline:
        "Securely manage disbursement and allocations with transparency across transactions",
    },
  };

  return (
    <section className="features" data-node-id="226:467">
      <div className="container features__inner">
        <div>
          <AnimatePresence mode="wait">
            <motion.p
              key={activeTab}
              className="features__lead leading-relaxed"
              data-node-id="223:144"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
            >
              {tabContent[activeTab].lead}
            </motion.p>
          </AnimatePresence>
          <div className="features__tabs" data-node-id="224:455">
            <button
              className={`tab ${
                activeTab === "documentation" ? "tab--active" : "tab--inactive"
              }`}
              type="button"
              data-node-id="224:452"
              onClick={() => setActiveTab("documentation")}
              aria-selected={activeTab === "documentation"}
              role="tab"
            >
              {tabContent.documentation.label}
            </button>
            <button
              className={`tab ${
                activeTab === "fees" ? "tab--active" : "tab--inactive"
              }`}
              type="button"
              data-node-id="224:453"
              onClick={() => setActiveTab("fees")}
              aria-selected={activeTab === "fees"}
              role="tab"
            >
              {tabContent.fees.label}
            </button>
            <button
              className={`tab ${
                activeTab === "disbursement" ? "tab--active" : "tab--inactive"
              }`}
              type="button"
              data-node-id="224:454"
              onClick={() => setActiveTab("disbursement")}
              aria-selected={activeTab === "disbursement"}
              role="tab"
            >
              {tabContent.disbursement.label}
            </button>
          </div>
        </div>

        <div className="features__content" data-node-id="226:466">
          <div className="features__text" data-node-id="226:465">
            <AnimatePresence mode="wait">
              <motion.h3
                key={activeTab}
                className="features__headline"
                data-node-id="224:458"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.24, ease: "easeOut" }}
              >
                {tabContent[activeTab].headline}
              </motion.h3>
            </AnimatePresence>
            <button
              className="button learn-more transition-transform duration-200 hover:-translate-y-0.5 active:translate-y-0"
              type="button"
              data-node-id="226:459"
            >
              Learn more
              <ArrowRight size={20} aria-hidden="true" />
            </button>
          </div>
          <AnimatePresence mode="wait">
            <motion.img
              key={activeTab}
              className="features__image rounded-2xl shadow-[var(--card-shadow)]"
              src={imgDashboard}
              alt="Dashboard preview"
              data-node-id="224:457"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.24, ease: "easeOut" }}
            />
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}

// Renamed WhyChooseUs to PlatformBenefits and updated its content
function PlatformBenefits() {
  return (
    <section className="why">
      {/* Platform Benefits Section */}
      <section className="features-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Your Platform Benefits</h2>
            <p className="section-subtitle">
              The official digital system for managing property transaction
              compliance across NBA branches in Lagos State.
            </p>
          </div>

          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-card__icon">
                <Shield size={32} />
              </div>
              <h3 className="feature-card__title">
                Fee Protection & Compliance
              </h3>
              <p className="feature-card__description">
                Your professional fees are automatically protected through
                AI-enhanced Fair Market Value verification. You retain 90-95% of
                NBA-mandated fees with systematic prevention of undercutting.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-card__icon">
                <Zap size={32} />
              </div>
              <h3 className="feature-card__title">Automated Disbursement</h3>
              <p className="feature-card__description">
                Professional fees settle directly to your registered bank
                account with same-day to T+1 processing. Complete transparency
                with detailed transaction records and audit trails.
              </p>
            </div>

            <div className="feature-card">
              <div className="feature-card__icon">
                <Users size={32} />
              </div>
              <h3 className="feature-card__title">Member Integration</h3>
              <p className="feature-card__description">
                Streamlined registration for all lawyers across NBA branches in
                Lagos State. Instant verification through NBA Enrollment Number
                ensures immediate access.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Fee Structure Section - New Addition */}
      <section className="bg-gray-50 py-20">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Fee Structure & Retention</h2>
            <p className="section-subtitle">
              Professional Fee Protection: You receive the full NBA-mandated 10%
              professional fee on all property transactions.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 shrink-0">
                  <CheckCircle size={24} />
                </div>
                <div>
                  <h4 className="font-bold text-lg mb-2">
                    Administrative Processing
                  </h4>
                  <p className="text-gray-600">
                    Small administrative fees (1% for properties ≤₦100M, 0.75%
                    for ₦100M-₦700M, 0.5% for {">"}₦700M) support platform
                    operations and security.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center text-green-600 shrink-0">
                  <Wallet size={24} />
                </div>
                <div>
                  <h4 className="font-bold text-lg mb-2">Your Net Retention</h4>
                  <p className="text-gray-600">
                    90-95% of your NBA professional fee settles directly to your
                    account (equivalent to 9-9.5% of total property value).
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
              <h3 className="text-xl font-bold mb-6 border-b pb-4">
                Example: ₦50M Property Transaction
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center text-gray-600">
                  <span>NBA Professional Fee</span>
                  <span className="font-medium">₦5,000,000</span>
                </div>
                <div className="flex justify-between items-center text-red-500">
                  <span>Administrative Fee</span>
                  <span className="font-medium">-₦50,000</span>
                </div>
                <div className="pt-4 border-t flex justify-between items-center text-lg font-bold text-[var(--nba-green)]">
                  <span>You Receive</span>
                  <span>₦4,950,000 (99%)</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </section>
  );
}

// Testimonial component removed

type FaqItem = { question: string; answer: string };

function AccordionPanel({
  id,
  isOpen,
  children,
}: {
  id: string;
  isOpen: boolean;
  children: React.ReactNode;
}) {
  const innerRef = React.useRef<HTMLDivElement | null>(null);
  const [height, setHeight] = React.useState(0);

  React.useLayoutEffect(() => {
    if (!innerRef.current) return;
    if (isOpen) {
      setHeight(innerRef.current.scrollHeight);
    } else {
      setHeight(0);
    }
  }, [isOpen, children]);

  return (
    <div
      id={id}
      className="accordion__panel"
      style={{ height }}
      aria-hidden={!isOpen}
    >
      <div ref={innerRef} className="accordion__panel-inner">
        {children}
      </div>
    </div>
  );
}

function FAQ() {
  const items: FaqItem[] = [
    {
      question: "What is a Remuneration Compliance software?",
      answer:
        "A remuneration compliance software helps apply approved fee standards, track transaction milestones, and create an auditable trail for payments and documentation.",
    },
    {
      question: "Who can use the portal?",
      answer:
        "Lawyers, law firms, and stakeholders involved in property transactions can use the portal to manage documentation, verification, and fee compliance.",
    },
    {
      question: "How does document verification work?",
      answer:
        "Documents are uploaded into a secure, centralised workflow where they can be validated, stored, and referenced across the lifecycle of a transaction.",
    },
    {
      question: "Is payment processing secure?",
      answer:
        "Yes. Payments can be tracked with clear allocations and compliance checks, improving transparency and reducing disputes.",
    },
    {
      question: "Can fees be higher than the minimum standard?",
      answer:
        "Yes. The platform enforces a minimum benchmark while allowing higher fees depending on transaction value and scope.",
    },
    {
      question: "Can I export records for reporting?",
      answer:
        "You can keep a structured record of transactions and documentation that supports reporting, audits, and compliance checks.",
    },
  ];

  const [openSet, setOpenSet] = React.useState<Set<number>>(() => new Set());

  const toggle = (idx: number) => {
    setOpenSet((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
      } else {
        next.add(idx);
      }
      return next;
    });
  };

  return (
    <section className="faq" data-node-id="235:744">
      <div className="container faq__inner">
        <h2 className="faq__title" data-node-id="235:743">
          Frequently
          <br />
          Asked
          <br />
          Question
        </h2>

        <div
          className="accordion"
          role="region"
          aria-label="Frequently Asked Questions"
        >
          {items.map((it, idx) => {
            const isOpen = openSet.has(idx);
            const panelId = `faq-panel-${idx}`;
            return (
              <div className="accordion__item" key={`${it.question}-${idx}`}>
                <button
                  className="accordion__button"
                  type="button"
                  aria-expanded={isOpen}
                  aria-controls={panelId}
                  onClick={() => toggle(idx)}
                >
                  <span>{it.question}</span>
                  <ChevronDown
                    className={isOpen ? "rotate-90" : ""}
                    size={20}
                    aria-hidden="true"
                  />
                </button>
                <AccordionPanel id={panelId} isOpen={isOpen}>
                  {it.answer}
                </AccordionPanel>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div data-node-id="191:1687">
      <Hero />
      <Partners />
      <Features />
      <Features />
      <PlatformBenefits />
      <FAQ />
    </div>
  );
}
